import { Device } from "@capacitor/device";
import { BASE_URL } from "../services/api";

// Define the structure for LogStringData
interface LogStringData {
  reconstructedLogString: string;
  event_id: string;
  batch_id: string;
}

// Interface for the result of a single log push
interface LogPushResult {
  event_id: string;
  batch_id: string;
  success: boolean;
  response?: string;
  error?: string;
  statusMessage: string; // Contains the "SRST,event_id,X" format
}

// The overall results of a batch push operation
interface BatchPushResult {
  statusMessages: string[];
  successCount: number;
  failureCount: number;
  detailedResults: LogPushResult[];
}

/**
 * Post a single log to the server
 *
 * @param logString The log string to post
 * @param event_id The event ID for this log
 * @param batch_id The batch ID this log belongs to
 * @returns Promise resolving to a LogPushResult
 */
async function postLogsToServer(
  logString: string,
  event_id: string,
  batch_id: string
): Promise<LogPushResult> {
  try {
    const APP_ID = await Device.getId();
    const response = await fetch(
      `${BASE_URL}/tesla.data.server/rest/receiver`,
      {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          "x-device-log-version": "2",
          "x-app-id": APP_ID.identifier, // ✅ new header
        },
        body: logString, // Sending logs as plain text
      }
    );

    if (!response.ok) {
      const statusMessage = `SRST,${event_id},0`;
      return {
        event_id,
        batch_id,
        success: false,
        error: `HTTP error! Status: ${response.status}`,
        statusMessage,
      };
    }

    const data = await response.text();
    console.log(`Server Response for event ${event_id}:`, data);
    const statusMessage = `SRST,${event_id},1`;

    return {
      event_id,
      batch_id,
      success: true,
      response: data,
      statusMessage,
    };
  } catch (error) {
    console.error(`Error posting log for event ${event_id}:`, error);
    const statusMessage = `SRST,${event_id},0`;

    return {
      event_id,
      batch_id,
      success: false,
      error: error instanceof Error ? error.message : String(error),
      statusMessage,
    };
  }
}

/**
 * Send all logs to server and return individual status messages for each event
 *
 * @param logStringList List of LogStringData objects to send
 * @returns Promise resolving to the batch push results
 */
export async function sendAllLogs(
  logStringList: LogStringData[]
): Promise<BatchPushResult> {
  // Make a copy of the logs to process
  const logsToProcess = [...logStringList];

  // Create an array to store the results
  const detailedResults: LogPushResult[] = [];

  // Process each log individually
  for (const logData of logsToProcess) {
    try {
      const result = await postLogsToServer(
        logData.reconstructedLogString,
        logData.event_id,
        logData.batch_id
      );

      detailedResults.push(result);
    } catch (error) {
      // Handle any unexpected errors
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      detailedResults.push({
        event_id: logData.event_id,
        batch_id: logData.batch_id,
        success: false,
        error: errorMessage,
        statusMessage: `SRST,${logData.event_id},0`,
      });
    }
  }

  // Calculate success and failure counts
  const successCount = detailedResults.filter(
    (result) => result.success
  ).length;
  const failureCount = detailedResults.length - successCount;

  // Extract all status messages
  const statusMessages = detailedResults.map((result) => result.statusMessage);

  // Clear the original logStringList if needed
  logStringList.length = 0;

  // Log results
  if (failureCount > 0) {
    console.error(`${failureCount} logs failed to send.`);
    console.error(
      "Failed logs:",
      detailedResults.filter((result) => !result.success)
    );
  } else {
    console.log("All logs sent successfully.");
  }

  return {
    statusMessages,
    successCount,
    failureCount,
    detailedResults,
  };
}

/**
 * Send logs for a specific batch
 *
 * @param logStringList List of LogStringData objects to send
 * @param batchId The batch ID to filter by
 * @returns Promise resolving to the batch push results
 */
export async function sendBatchLogs(
  logStringList: LogStringData[],
  batchId: string
): Promise<BatchPushResult> {
  // Filter logs by batch ID
  const batchLogs = logStringList.filter((log) => log.batch_id === batchId);
  return sendAllLogs(batchLogs);
}

/**
 * Send logs for a specific event
 *
 * @param logStringList List of LogStringData objects to send
 * @param eventId The event ID to send
 * @returns Promise resolving to the batch push results
 */
export async function sendEventLog(
  logStringList: LogStringData[],
  eventId: string
): Promise<BatchPushResult> {
  // Filter logs by event ID
  const eventLogs = logStringList.filter((log) => log.event_id === eventId);
  return sendAllLogs(eventLogs);
}
